/* eslint-disable */
import {
  Badge,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import Card from 'components/card/Card';
import React, { useEffect, useState } from 'react';
import CompaniesApi from '../../../../api/companies';
import AddNewCompanyModal from './AddNewCompanyModal';
import CompanyDetailsModal from './CompanyDetailsModal';

export default function CompaniesTable() {
  const [data, setData] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [expandedOrgs, setExpandedOrgs] = useState(new Set());
  const [search, setSearch] = useState('');

  const refreshCompanies = () => {
    CompaniesApi.getAll()
      .then((res) => setData(res.data))
      .catch(() => {});
  };

  useEffect(() => {
    refreshCompanies();
  }, []);

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const {
    isOpen: isCompanyDetailsOpen,
    onOpen: onCompanyDetailsOpen,
    onClose: onCompanyDetailsClose,
  } = useDisclosure();

  const {
    isOpen: isNewCompanyOpen,
    onOpen: onNewCompanyOpen,
    onClose: onNewCompanyClose,
  } = useDisclosure();

  const handleDetailsClicked = (company) => {
    setSelectedCompany(company);
    onCompanyDetailsOpen();
  };

  const toggleOrg = (orgId) => {
    setExpandedOrgs((prev) => {
      const next = new Set(prev);
      next.has(orgId) ? next.delete(orgId) : next.add(orgId);
      return next;
    });
  };

  const searchTerm = search.trim().toLowerCase();

  const orgMap = new Map();
  const ungrouped = [];

  data.forEach((company) => {
    if (searchTerm && !company.name.toLowerCase().includes(searchTerm) && !String(company.id).includes(searchTerm)) return;
    if (company.organization?.id) {
      if (!orgMap.has(company.organization.id)) {
        orgMap.set(company.organization.id, {
          id: company.organization.id,
          name: company.organization.name,
          companies: [],
        });
      }
      orgMap.get(company.organization.id).companies.push(company);
    } else {
      ungrouped.push(company);
    }
  });

  const orgGroups = [...orgMap.values()].sort((a, b) =>
    a.name.localeCompare(b.name, 'he'),
  );

  // Demo rows pin to the very top — above all organizations and ungrouped
  // companies. They're synthetic for sales demos and should be one click away.
  const demoCompanies = ungrouped.filter((c) => c.isDemo);
  const realUngrouped = ungrouped.filter((c) => !c.isDemo);

  const renderCompanyRow = (company, insideOrg = false) => (
    <Tr
      key={company.id}
      bg={company.isDemo ? 'purple.50' : insideOrg ? 'gray.50' : undefined}
    >
      <Td fontSize="sm" borderColor="transparent" width="10px" ps={insideOrg ? '40px' : undefined}>
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {company.id}
        </Text>
      </Td>
      <Td fontSize="sm" borderColor="transparent" width="150px">
        <Flex align="center" gap={2}>
          <Text color={textColor} fontSize="sm" fontWeight="700">
            {company.name}
          </Text>
          {company.isDemo && (
            <Badge colorScheme="purple" borderRadius="full" fontSize="10px">
              דמו
            </Badge>
          )}
        </Flex>
      </Td>
      <Td fontSize="sm" borderColor="transparent" width="150px">
        {insideOrg ? (
          <Text color="gray.400" fontSize="sm">—</Text>
        ) : company.isDemo ? (
          <Text color="purple.600" fontSize="sm" fontWeight="700">
            דמו פנימי
          </Text>
        ) : (
          <Text
            color={company.organization?.name ? textColor : 'gray.400'}
            fontSize="sm"
            fontWeight="700"
          >
            {company.organization?.name || 'N/A'}
          </Text>
        )}
      </Td>
      <Td fontSize="sm" borderColor="transparent" width="150px">
        <Flex align="center">
          <Button variant="brand" onClick={() => handleDetailsClicked(company)}>
            פרטים
          </Button>
        </Flex>
      </Td>
    </Tr>
  );

  return (
    <Card direction="column" w="100%" px="0px" overflowX={{ sm: 'scroll', lg: 'hidden' }}>
      <Flex px="25px" mb="20px" align="center" gap={3}>
        <Button variant="blue" onClick={onNewCompanyOpen}>
          הוספת חברה
        </Button>
        <InputGroup maxW="260px">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" boxSize={3.5} />
          </InputLeftElement>
          <Input
            placeholder="חיפוש חברה..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="sm"
            borderRadius="md"
            fontSize="sm"
          />
        </InputGroup>
      </Flex>

      <AddNewCompanyModal
        callBack={refreshCompanies}
        isOpen={isNewCompanyOpen}
        onClose={onNewCompanyClose}
      />
      <CompanyDetailsModal
        company={selectedCompany}
        isOpen={isCompanyDetailsOpen}
        onClose={onCompanyDetailsClose}
      />

      <Table variant="simple" color="gray.500" mb="24px">
        <Thead>
          <Tr>
            {[
              { label: 'מס׳ חברה', width: '10px' },
              { label: 'חברה', width: '150px' },
              { label: 'אירגון', width: '150px' },
              { label: 'פעולות נוספות', width: '150px' },
            ].map(({ label, width }) => (
              <Th key={label} pe="10px" borderColor={borderColor} width={width}>
                <Flex
                  justify="space-between"
                  align="center"
                  fontSize={{ sm: '10px', lg: '12px' }}
                  color="gray.400"
                >
                  {label}
                </Flex>
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {/* Pinned demo companies — above everything else */}
          {demoCompanies.map((company) => renderCompanyRow(company))}

          {/* Organization groups */}
          {orgGroups.map((org) => {
            const isExpanded = searchTerm ? true : expandedOrgs.has(org.id);
            return (
              <React.Fragment key={`org-${org.id}`}>
                <Tr
                  onClick={() => toggleOrg(org.id)}
                  cursor="pointer"
                  bg="blue.50"
                  _hover={{ bg: 'blue.100' }}
                >
                  <Td colSpan={4} borderColor="transparent" py="10px">
                    <Flex align="center" gap={2}>
                      {isExpanded ? (
                        <ChevronUpIcon boxSize={4} color="blue.500" />
                      ) : (
                        <ChevronDownIcon boxSize={4} color="blue.500" />
                      )}
                      <Text fontWeight="bold" color="blue.700" fontSize="sm">
                        {org.name}
                      </Text>
                      <Badge colorScheme="blue" borderRadius="full">
                        {org.companies.length}
                      </Badge>
                    </Flex>
                  </Td>
                </Tr>
                {isExpanded && org.companies.map((company) => renderCompanyRow(company, true))}
              </React.Fragment>
            );
          })}

          {/* Ungrouped companies (no organization) */}
          {realUngrouped.map((company) => renderCompanyRow(company))}
        </Tbody>
      </Table>
    </Card>
  );
}
